import React, { useState, useCallback } from 'react';
import { Shield, Loader2, AlertCircle, RefreshCw, Info, Package, Link2, LayoutList, GitBranch, BarChart3, Map, Grid3x3 } from 'lucide-react';
import { useCisaAssessment } from '../hooks/useCisaAssessment';
import { useTechStackSync } from '../hooks/useTechStackSync';
import useCapabilityAssessment from '../hooks/useCapabilityQuestions';
import { CISA_PILLARS, CROSS_CUTTING, PRODUCT_STATUS, MATURITY_CRITERIA } from '../config/cisaMaturityModel';
import CisaFunctionCardList from './CisaFunctionCardList';
import CisaPillarSummary from './CisaPillarSummary';
import CisaDashboardHeader from './CisaDashboardHeader';
import CisaPillarAssessment from './CisaPillarAssessment';
import ArchitectureDiagram from './ArchitectureDiagram';
import ZeroTrustJourney from './ZeroTrustJourney';
import CisaMatrixView from './CisaMatrixView';
import { AssessmentModal } from './shared';
import './CisaMaturityModel.css';

/**
 * CISA Zero Trust Maturity Model v2.0 Assessment
 *
 * Redesigned with expandable card list layout providing:
 * - Dashboard view with overall scores and pillar cards
 * - Clear progress visualization per function
 * - Inline product selection with status tracking
 * - Target level setting for gap analysis
 * - Configuration steps for maturity progression
 * - Sync with ZT Architecture tech stack (derived levels)
 * - Pillar deep-dive assessment with 6 subtabs
 */
const CisaMaturityModel = ({ selectedDepartment }) => {
  const departmentId = selectedDepartment?.department_id;

  // View mode: 'dashboard' | 'cards' | 'architecture'
  const [viewMode, setViewMode] = useState('dashboard');

  // Selected pillar for deep-dive assessment (null = dashboard view)
  const [selectedPillar, setSelectedPillar] = useState(null);

  // Selected function for Matrix view assessment modal
  const [matrixSelectedFunction, setMatrixSelectedFunction] = useState(null);

  const {
    assessments,
    targetLevels,
    productAdoptions,
    configCompletions,
    summary,
    loading,
    saving,
    error,
    updateCell,
    updateTargetLevel,
    adoptProduct,
    updateProductStatus,
    removeProduct,
    toggleConfigStep,
    resetAll,
    useLocalStorage
  } = useCisaAssessment(departmentId);

  const { hasAnyTechStack } = useTechStackSync(departmentId);

  // Capability-based assessment (new capability-first approach)
  const {
    questions: capabilityQuestions,
    answersMap: capabilityAnswers,
    derivedLevels: capabilityDerivedLevels,
    isLoading: capabilityLoading,
    isSaving: capabilitySaving,
    saveAnswer: saveCapabilityAnswer,
    getDerivedLevel,
    setFunctionOverride
  } = useCapabilityAssessment(departmentId, matrixSelectedFunction?.functionCode);

  // Handle pillar selection
  const handleSelectPillar = (pillarCode) => {
    setSelectedPillar(pillarCode);
  };

  // Handle back from pillar assessment
  const handleBackFromPillar = () => {
    setSelectedPillar(null);
  };

  // Handle matrix cell click to open assessment modal
  const handleMatrixCellClick = (functionCode, level) => {
    // Find the pillar code and function name for this function
    let pillarCode = 'IDENTITY';
    let functionName = functionCode;
    let description = '';

    for (const [pCode, pillarData] of Object.entries(summary.pillars || {})) {
      const fn = pillarData.functions?.find(f => f.code === functionCode);
      if (fn) {
        pillarCode = pCode;
        functionName = fn.name || functionCode;
        description = fn.description || '';
        break;
      }
    }

    // Also check cross-cutting
    const ccMatch = CROSS_CUTTING.find(cc => cc.code === functionCode);
    if (ccMatch) {
      pillarCode = 'CROSS_CUTTING';
      functionName = ccMatch.name;
    }

    setMatrixSelectedFunction({ functionCode, level, pillarCode, functionName, description });
  };

  // Close the assessment modal
  const handleCloseAssessmentModal = useCallback(() => {
    setMatrixSelectedFunction(null);
  }, []);

  // Handle capability answer change
  const handleCapabilityAnswerChange = useCallback((questionId, answer, evidenceNotes) => {
    saveCapabilityAnswer(questionId, answer, evidenceNotes);
  }, [saveCapabilityAnswer]);

  // Handle setting target level from modal
  const handleSetTargetLevel = useCallback((level) => {
    if (matrixSelectedFunction?.functionCode) {
      updateTargetLevel(matrixSelectedFunction.functionCode, level);
    }
  }, [matrixSelectedFunction, updateTargetLevel]);

  // Handle maturity override from modal
  const handleOverrideLevel = useCallback(() => {
    // For now, just log - could open a sub-modal for override reason
    console.log('Override requested for', matrixSelectedFunction?.functionCode);
  }, [matrixSelectedFunction]);

  if (loading) {
    return (
      <div className="cisa-loading">
        <Loader2 size={28} className="cisa-spin" />
        <p>Loading CISA Maturity Assessment...</p>
      </div>
    );
  }

  if (error && error.status !== 404) {
    return (
      <div className="cisa-error">
        <AlertCircle size={28} />
        <p>Failed to load assessment: {error.message}</p>
      </div>
    );
  }

  // Render pillar deep-dive assessment
  if (selectedPillar) {
    return (
      <div className="cisa-container">
        <CisaPillarAssessment
          pillarCode={selectedPillar}
          assessments={assessments}
          targetLevels={targetLevels}
          productAdoptions={productAdoptions}
          configCompletions={configCompletions}
          summary={summary}
          onBack={handleBackFromPillar}
          onUpdateLevel={updateCell}
          onUpdateTargetLevel={updateTargetLevel}
          onAdoptProduct={adoptProduct}
          onUpdateProductStatus={updateProductStatus}
          onRemoveProduct={removeProduct}
          onToggleConfigStep={toggleConfigStep}
        />
      </div>
    );
  }

  return (
    <div className="cisa-container">
      {/* Header toolbar */}
      <div className="cisa-toolbar">
        <div className="cisa-toolbar-left">
          <Shield size={18} style={{ color: 'var(--color-primary)' }} />
          <span className="cisa-title">CISA Zero Trust Maturity Model v2.0</span>
          {saving && (
            <span className="cisa-saving">
              <Loader2 size={14} className="cisa-spin" />
              Saving...
            </span>
          )}

          {/* View mode tabs */}
          <div className="cisa-view-tabs">
            <button
              type="button"
              className={`cisa-view-tab ${viewMode === 'dashboard' ? 'cisa-view-tab--active' : ''}`}
              onClick={() => setViewMode('dashboard')}
              title="Dashboard Overview"
            >
              <BarChart3 size={14} />
              <span>Dashboard</span>
            </button>
            <button
              type="button"
              className={`cisa-view-tab ${viewMode === 'cards' ? 'cisa-view-tab--active' : ''}`}
              onClick={() => setViewMode('cards')}
              title="Assessment Cards"
            >
              <LayoutList size={14} />
              <span>Cards</span>
            </button>
            <button
              type="button"
              className={`cisa-view-tab ${viewMode === 'architecture' ? 'cisa-view-tab--active' : ''}`}
              onClick={() => setViewMode('architecture')}
              title="Architecture Diagram"
            >
              <GitBranch size={14} />
              <span>Architecture</span>
            </button>
            <button
              type="button"
              className={`cisa-view-tab ${viewMode === 'journey' ? 'cisa-view-tab--active' : ''}`}
              onClick={() => setViewMode('journey')}
              title="Maturity Journey"
            >
              <Map size={14} />
              <span>Journey</span>
            </button>
            <button
              type="button"
              className={`cisa-view-tab ${viewMode === 'matrix' ? 'cisa-view-tab--active' : ''}`}
              onClick={() => setViewMode('matrix')}
              title="Workshop Matrix - Complete posture view"
            >
              <Grid3x3 size={14} />
              <span>Matrix</span>
            </button>
          </div>
        </div>

        <div className="cisa-toolbar-right">
          {hasAnyTechStack && (
            <span className="cisa-sync-badge" title="Synced with this department's Zero Trust Architecture stack">
              <Link2 size={11} />
              Synced with stack
            </span>
          )}
          {useLocalStorage && (
            <span className="cisa-local-badge" title="Data stored locally in browser">
              Local Storage
            </span>
          )}
          {selectedDepartment && (
            <span className="cisa-dept-badge">
              {selectedDepartment.acronym || selectedDepartment.official_name}
            </span>
          )}
          <button
            type="button"
            className="cisa-reset-btn"
            onClick={resetAll}
            title="Reset all assessments to Traditional"
          >
            <RefreshCw size={14} />
            Reset
          </button>
        </div>
      </div>

      {/* Legend - show for cards and architecture views */}
      {viewMode !== 'dashboard' && (
        <div className="cisa-legend">
          <div className="cisa-legend-section">
            <span className="cisa-legend-title">Maturity Levels:</span>
            <div className="cisa-legend-items">
              <span className="cisa-legend-item cisa-level--traditional">
                <span className="cisa-swatch" />
                Traditional
              </span>
              <span className="cisa-legend-item cisa-level--initial">
                <span className="cisa-swatch" />
                Initial
              </span>
              <span className="cisa-legend-item cisa-level--advanced">
                <span className="cisa-swatch" />
                Advanced
              </span>
              <span className="cisa-legend-item cisa-level--optimal">
                <span className="cisa-swatch" />
                Optimal
              </span>
            </div>
          </div>

          <div className="cisa-legend-section cisa-legend-section--status">
            <span className="cisa-legend-title">Product Status:</span>
            <div className="cisa-legend-items">
              {PRODUCT_STATUS.map(s => (
                <span key={s.key} className="cisa-legend-item">
                  <span className="cisa-swatch" style={{ background: s.color }} />
                  {s.label}
                </span>
              ))}
            </div>
          </div>

          <button
            type="button"
            className="cisa-info-btn"
            title="About CISA ZTMM"
            onClick={() =>
              window.open('https://www.cisa.gov/zero-trust-maturity-model', '_blank')
            }
          >
            <Info size={14} />
          </button>
        </div>
      )}

      {/* View mode content */}
      {viewMode === 'dashboard' ? (
        <>
          {/* Dashboard header with overall score and pillar cards */}
          <CisaDashboardHeader
            summary={summary}
            onSelectPillar={handleSelectPillar}
          />

          {/* Quick stats (shown below dashboard header) */}
          {(summary.totalProductsAdopted > 0 || summary.functionsWithGaps > 0) && (
            <div className="cisa-quick-stats">
              {summary.functionsWithGaps > 0 && (
                <div className="cisa-quick-stat">
                  <span className="cisa-quick-stat-value">{summary.functionsWithGaps}</span>
                  <span className="cisa-quick-stat-label">Functions with gaps</span>
                </div>
              )}
              {summary.totalProductsAdopted > 0 && (
                <div className="cisa-quick-stat">
                  <Package size={14} />
                  <span className="cisa-quick-stat-value">{summary.totalProductsAdopted}</span>
                  <span className="cisa-quick-stat-label">Products tracked</span>
                </div>
              )}
            </div>
          )}

          {/* Pillar summary scores */}
          <CisaPillarSummary
            pillars={CISA_PILLARS}
            crossCutting={CROSS_CUTTING}
            summary={summary}
          />
        </>
      ) : viewMode === 'cards' ? (
        <>
          {/* Summary stats (quick overview) */}
          {summary.totalProductsAdopted > 0 || summary.functionsWithGaps > 0 ? (
            <div className="cisa-quick-stats">
              {summary.functionsWithGaps > 0 && (
                <div className="cisa-quick-stat">
                  <span className="cisa-quick-stat-value">{summary.functionsWithGaps}</span>
                  <span className="cisa-quick-stat-label">Functions with gaps</span>
                </div>
              )}
              {summary.totalProductsAdopted > 0 && (
                <div className="cisa-quick-stat">
                  <Package size={14} />
                  <span className="cisa-quick-stat-value">{summary.totalProductsAdopted}</span>
                  <span className="cisa-quick-stat-label">Products tracked</span>
                </div>
              )}
            </div>
          ) : null}

          {/* Main card list */}
          <CisaFunctionCardList
            assessments={assessments}
            productAdoptions={productAdoptions}
            onCurrentLevelChange={updateCell}
            onTargetLevelChange={updateTargetLevel}
            onAdoptProduct={adoptProduct}
            onUpdateProductStatus={updateProductStatus}
            onRemoveProduct={removeProduct}
          />

          {/* Pillar summary scores */}
          <CisaPillarSummary
            pillars={CISA_PILLARS}
            crossCutting={CROSS_CUTTING}
            summary={summary}
          />
        </>
      ) : viewMode === 'architecture' ? (
        /* Architecture Diagram view */
        <ArchitectureDiagram
          selectedDepartment={selectedDepartment}
          productAdoptions={productAdoptions}
          onAdoptProduct={adoptProduct}
          onRemoveProduct={removeProduct}
        />
      ) : viewMode === 'journey' ? (
        /* Journey view - Maturity progression diagrams */
        <ZeroTrustJourney
          departmentId={departmentId}
          assessments={assessments}
          targetLevels={targetLevels}
          productAdoptions={productAdoptions}
          onUpdateLevel={updateCell}
          onUpdateTargetLevel={updateTargetLevel}
          onAdoptProduct={adoptProduct}
          onRemoveProduct={removeProduct}
        />
      ) : (
        /* Matrix view - Workshop deliverable with complete posture */
        <div className="cisa-matrix-wrapper">
          <CisaMatrixView
            assessments={assessments}
            targetLevels={targetLevels}
            showCrossCutting={true}
            onCellClick={handleMatrixCellClick}
          />

          {/* Assessment Modal - capability-first assessment with tabbed navigation */}
          <AssessmentModal
            isOpen={!!matrixSelectedFunction}
            onClose={handleCloseAssessmentModal}
            functionCode={matrixSelectedFunction?.functionCode}
            functionName={matrixSelectedFunction?.functionName}
            pillarCode={matrixSelectedFunction?.pillarCode}
            description={matrixSelectedFunction?.description}
            criteria={MATURITY_CRITERIA[matrixSelectedFunction?.functionCode]}
            questions={capabilityQuestions}
            answers={capabilityAnswers}
            currentLevel={assessments[matrixSelectedFunction?.functionCode]}
            targetLevel={targetLevels[matrixSelectedFunction?.functionCode]}
            derivedLevel={getDerivedLevel(matrixSelectedFunction?.functionCode)}
            onAnswerChange={handleCapabilityAnswerChange}
            onSetTargetLevel={handleSetTargetLevel}
            onOverrideLevel={handleOverrideLevel}
            isLoading={capabilityLoading}
            isSaving={capabilitySaving}
          />
        </div>
      )}
    </div>
  );
};

export default CisaMaturityModel;
